'use client'

import { useState } from 'react'
import { WishlistItem } from '@/lib/hooks/useWishlist'
import { ct } from '@/lib/i18n/configurator'

interface WishlistSidebarProps {
  wishlists: WishlistItem[]
  currentBaseImage?: string
  onLoadConfig: (wcIds: number[], funnelSlug?: string) => void
  onDelete: (id: string) => void
  maxItems: number
}

export function WishlistSidebar({
  wishlists,
  currentBaseImage,
  onLoadConfig,
  onDelete,
  maxItems,
}: WishlistSidebarProps) {
  const [isOpen, setIsOpen] = useState(false)

  if (wishlists.length === 0) return null

  // Split wishlists into available and unavailable
  const availableWishlists = wishlists.filter(w => !w.has_unavailable_products)
  const unavailableWishlists = wishlists.filter(w => w.has_unavailable_products)

  const renderWishlistItem = (item: WishlistItem, isUnavailable: boolean) => {
    // Use the item's base_image (from its funnel) or fall back to current
    const itemBaseImage = item.base_image || currentBaseImage
    // Use product_images from the API (already in the correct size for this wishlist)
    const productImages = item.product_images || []

    return (
      <div
        key={item.id}
        className="relative group"
      >
        {/* Mini preview */}
        <div
          className={`w-full aspect-square bg-gray-100 rounded-lg overflow-hidden relative ${
            isUnavailable ? '' : 'hover:ring-2 hover:ring-pink-400 cursor-pointer'
          } transition-all`}
          onClick={() => !isUnavailable && onLoadConfig(item.config, item.funnel_slug)}
        >
          {/* Base image (from the item's funnel) */}
          {itemBaseImage && (
            <img
              src={itemBaseImage}
              alt=""
              className={`absolute inset-0 w-full h-full object-contain transition-all ${isUnavailable ? 'blur-[4px]' : ''}`}
            />
          )}
          {/* Product layers (using pre-fetched images in the correct size) */}
          {productImages.map(p => p.image && (
            <img
              key={p.wcId}
              src={p.image}
              alt=""
              className={`absolute inset-0 w-full h-full object-contain transition-all ${isUnavailable ? 'blur-[4px]' : ''}`}
              style={{ zIndex: p.layer }}
            />
          ))}

          {/* Unavailable overlay */}
          {isUnavailable && (
            <>
              <div className="absolute inset-0 bg-gray-900/60 z-[45]" />
              <div className="absolute inset-0 flex items-center justify-center z-[46]">
                <div className="text-center px-2">
                  <p className="text-xs font-bold text-white leading-tight">
                    {ct('popular')}
                  </p>
                  <p className="text-[10px] text-white leading-tight mt-1">
                    {ct('setOutOfStock')}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Delete button - always visible for unavailable items */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete(item.id)
          }}
          className={`absolute -top-1 -right-1 z-50 text-white rounded-full w-5 h-5 flex items-center justify-center transition-opacity text-xs ${
            isUnavailable ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          style={{ backgroundColor: '#9b2b2a' }}
          title={ct('remove')}
        >
          ×
        </button>

        {/* Price */}
        <p className={`text-xs text-center mt-1 ${isUnavailable ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
          {(item.total_price / 100).toFixed(0)}€
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Toggle button - always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-white shadow-lg rounded-r-lg p-3 transition-all ${
          isOpen ? 'translate-x-[140px]' : 'translate-x-0'
        }`}
        title={isOpen ? ct('closeWishlist') : ct('seeWishlist')}
      >
        <svg
          className={`w-6 h-6 text-pink-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
        <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
          {wishlists.length}
        </span>
      </button>

      {/* Sidebar panel */}
      <div
        className={`fixed left-0 top-0 h-full w-[140px] bg-white shadow-xl z-30 transition-transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="p-3 border-b text-center">
          <div className="flex items-center justify-center gap-1">
            <svg className="w-4 h-4 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span className="text-sm font-medium text-gray-900">{ct('wishlist')}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{wishlists.length}/{maxItems}</p>
        </div>

        {/* Wishlist items */}
        <div className="overflow-y-auto h-[calc(100%-50px)] p-2 space-y-2">
          {/* Available items */}
          {availableWishlists.map(item => renderWishlistItem(item, false))}

          {/* Unavailable section */}
          {unavailableWishlists.length > 0 && (
            <>
              {availableWishlists.length > 0 && (
                <div className="pt-2 pb-1">
                  <div className="border-t border-gray-200" />
                </div>
              )}
              <p className="text-[9px] text-gray-400 text-center font-medium uppercase tracking-wide">
                {ct('outOfStock')}
              </p>
              {unavailableWishlists.map(item => renderWishlistItem(item, true))}
            </>
          )}
        </div>
      </div>

      {/* Overlay when open on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-20 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
